import { useCallback, useState } from "react";
import { toast } from "sonner";
import type { Segment } from "@/types/spyserp";
import {
  useDeleteSpyserpSegment,
  useSaveSpyserpSegment,
  useSpyserpSegments,
} from "@/hooks/useSpyserpSegments";

export const useSegments = () => {
  const { data: segments = [], isPending: isLoading } = useSpyserpSegments();
  const saveSegment = useSaveSpyserpSegment();
  const deleteSegment = useDeleteSpyserpSegment();

  const [editingSegment, setEditingSegment] = useState<Segment | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [segmentToDelete, setSegmentToDelete] = useState<string | null>(null);

  const isFormOpen = isCreating || editingSegment !== null;

  const startCreate = useCallback(() => {
    setEditingSegment(null);
    setIsCreating(true);
  }, []);

  const startEdit = useCallback((segment: Segment) => {
    setEditingSegment(segment);
    setIsCreating(false);
  }, []);

  const cancelForm = useCallback(() => {
    setEditingSegment(null);
    setIsCreating(false);
  }, []);

  const requestDelete = useCallback((id: string) => {
    setSegmentToDelete(id);
  }, []);

  const cancelDelete = useCallback(() => {
    setSegmentToDelete(null);
  }, []);

  const confirmDelete = useCallback(() => {
    if (!segmentToDelete) return;

    deleteSegment.mutate(segmentToDelete, {
      onSuccess: () => setSegmentToDelete(null),
      onError: (error) => {
        console.error("Error deleting segment:", error);
        toast.error("Error deleting segment!");
      },
    });
  }, [deleteSegment, segmentToDelete]);

  return {
    segments,
    isLoading,
    isFormOpen,
    isCreating,
    editingSegment,
    segmentToDelete,
    saveSegment,
    deleteSegment,
    startCreate,
    startEdit,
    cancelForm,
    requestDelete,
    cancelDelete,
    confirmDelete,
  };
};
